import React from 'react';

const PrivacyAndTerms = () => {
    return (
        <div className="min-h-screen bg-linear-to-b from-gray-900 to-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                {/* Page header */}
                <div className="text-center mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Privacy Policy&nbsp;&amp;&nbsp;Terms&nbsp;of&nbsp;Service
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                        Please read this document carefully—by using <strong>olaid</strong> you agree to both our Privacy Policy
                        and our Terms of Service.
                    </p>
                </div>

                {/* Main content card */}
                <div className="bg-secondary-dark rounded-lg p-8 space-y-12 text-gray-300">
                    {/* 1 Introduction */}
                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">1.&nbsp;Introduction</h2>
                        <p>
                            At <strong>olaid</strong>, we value your privacy and security. This Privacy Policy and Terms of Service
                            outlines how we collect, use, share, and protect your personal information when you use our app. By
                            accessing or using olaid, you agree to the terms and conditions set forth herein.
                        </p>
                    </section>

                    {/* 2 Data Collection & Use */}
                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">2.&nbsp;Data Collection&nbsp;and&nbsp;Use</h2>

                        <p className="font-semibold mb-2">Personal&nbsp;Data:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Contact information: Name, email address, phone number.</li>
                            <li>
                                Health information: Complete medical history, allergies, medications, chronic diseases, blood
                                information, etc.
                            </li>
                            <li>
                                Emergency contact information: Names, email addresses, and phone numbers of your emergency contacts.
                            </li>
                            <li>
                                Usage data: Information about how you use the app—features accessed, frequency of use, and device
                                characteristics.
                            </li>
                            <li>
                                Legal Basis for Processing: Contractual necessity (Art.&nbsp;6 (1)(b) GDPR), legal obligations (Art.&nbsp;6
                                (1)(c)), legitimate interests (Art.&nbsp;6 (1)(f)), and—where required—your consent (Art.&nbsp;6 (1)(a)).
                            </li>
                            <li>
                                Consent Management: By providing your personal data you give explicit consent. You may withdraw consent
                                at any time (Art.&nbsp;7 (3) GDPR).
                            </li>
                            <li>
                                Data Retention: We keep data only as long as needed for the purposes described here or as required by
                                law (Art.&nbsp;5 (1)(e) GDPR).
                            </li>
                        </ul>

                        <p className="mt-4">
                            This data is collected and processed in compliance with the General Data Protection Regulation (GDPR),
                            ensuring transparency, lawful processing, and respect for individual rights.
                        </p>

                        <p className="font-semibold mt-6 mb-2">Use of data:</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>
                                <strong>Provide Services:</strong> Store your health information, allow quick access in emergencies, and
                                facilitate communication with emergency services.
                            </li>
                            <li>
                                <strong>Personalize Your Experience:</strong> Tailor the app to your specific needs and offer relevant
                                content.
                            </li>
                            <li>
                                <strong>Improve the App:</strong> Analyze usage data to identify areas for improvement and develop new
                                features.
                            </li>
                            <li>
                                <strong>Comply with Legal Obligations:</strong> Maintain records for legal and tax purposes.
                            </li>
                        </ul>
                    </section>

                    {/* 3 Data Security */}
                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">3.&nbsp;Data&nbsp;Security</h2>
                        <p>
                            The security of your data is our top priority. In compliance with GDPR Art.&nbsp;32, we employ technical
                            and organizational measures, including:
                        </p>
                        <ul className="list-disc pl-6 mt-3 space-y-2">
                            <li>Restricted Access: Only authorized personnel may access your data.</li>
                            <li>Access Controls: Role-based access controls are enforced.</li>
                            <li>Continuous Monitoring: Systems are monitored for security threats 24/7.</li>
                            <li>Regular Security Audits and penetration tests to detect vulnerabilities.</li>
                            <li>End-to-end encryption for all stored data.</li>
                            <li>Secure, encrypted servers with regular backups.</li>
                        </ul>
                    </section>

                    {/* 4 Data Sharing */}
                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">4.&nbsp;Data&nbsp;Sharing</h2>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>
                                <strong>With Your Consent:</strong> We share data with third parties only with your explicit
                                consent&nbsp;(Art.&nbsp;7 GDPR). All processors sign DPAs (Art.&nbsp;28).
                            </li>
                            <li>Service Providers: Trusted vendors (cloud, analytics) help us operate the app.</li>
                            <li>
                                Emergency Services: In emergencies we may share your information with ambulances, fire departments, or
                                police.
                            </li>
                            <li>Legal Compliance: Disclosure if required by law or government agencies.</li>
                            <li>
                                Cross-Border Transfers: When data leaves the EEA, we rely on SCCs or adequacy decisions (GDPR Chapter V).
                            </li>
                        </ul>
                    </section>

                    {/* 5 User Eligibility & Subscription */}
                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">
                            5.&nbsp;User&nbsp;Eligibility&nbsp;and&nbsp;Subscription
                        </h2>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>
                                <strong>Age Restrictions:</strong> Users under 18 need parental or guardian consent. Age-gating may be
                                used.
                            </li>
                            <li>
                                <strong>Subscription:</strong> One-week free trial, then USD 25 + VAT per year, auto-renewing unless
                                canceled.
                            </li>
                        </ul>
                    </section>

                    {/* 6 User Responsibilities */}
                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">6.&nbsp;User&nbsp;Responsibilities</h2>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Provide accurate medical information for emergency use.</li>
                            <li>
                                olaid is not responsible for decisions made by emergency responders. We are not liable for resulting
                                damages or injuries.
                            </li>
                            {/* Extra duties from TermsOfService */}
                            <li>Maintain confidentiality of your account credentials.</li>
                            <li>Update your medical information as needed.</li>
                            <li>Use the service in compliance with all applicable laws.</li>
                            <li>Do not share your QR code or credentials with unauthorized parties.</li>
                        </ul>
                    </section>

                    {/* 7 User Rights (GDPR + GeneralPolicy rights) */}
                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">7.&nbsp;User&nbsp;Rights</h2>
                        <p>You have the following rights under GDPR and our policy:</p>
                        <ul className="list-disc pl-6 mt-3 space-y-2">
                            <li>Right to Access (Art.&nbsp;15) – obtain copies of your data.</li>
                            <li>Right to Rectification (Art.&nbsp;16) – correct inaccurate data.</li>
                            <li>Right to Erasure (Art.&nbsp;17) – request deletion, subject to obligations.</li>
                            <li>Right to Restrict Processing (Art.&nbsp;18).</li>
                            <li>Right to Data Portability (Art.&nbsp;20).</li>
                            <li>Right to Object (Art.&nbsp;21).</li>
                            <li>Right to Withdraw Consent (Art.&nbsp;7 (3)).</li>
                            <li>Right to Lodge a Complaint with a supervisory authority.</li>
                            {/* From GeneralPolicy */}
                            <li>Export your data in a portable format.</li>
                            <li>Delete your account and associated data.</li>
                        </ul>
                    </section>

                    {/* 8 Cancellation & Refunds */}
                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">8.&nbsp;Cancellation&nbsp;and&nbsp;Refunds</h2>
                        <p>You can cancel your subscription at any time, but refunds are not offered once payment has been processed.</p>
                    </section>

                    {/* 9 Changes to Terms */}
                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">
                            9.&nbsp;Changes&nbsp;to&nbsp;Terms&nbsp;of&nbsp;Service
                        </h2>
                        <p>
                            olaid reserves the right to update these terms and policies when necessary. We will notify you of
                            significant changes, and continued use of the service after an update constitutes acceptance of the
                            revised terms.
                        </p>
                    </section>

                    {/* Additional Terms-specific sections */}
                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">10.&nbsp;Medical&nbsp;Disclaimer</h2>
                        <p>olaid is not a substitute for professional medical care. You acknowledge that:</p>
                        <ul className="list-disc pl-6 mt-3 space-y-2">
                            <li>The service is for information storage and sharing only.</li>
                            <li>We do not provide medical advice or treatment.</li>
                            <li>In an emergency, always contact emergency services first.</li>
                            <li>The accuracy of information depends on user input.</li>
                            <li>Healthcare providers may require additional information.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">11.&nbsp;Account&nbsp;Management</h2>
                        <p>We reserve the right to:</p>
                        <ul className="list-disc pl-6 mt-3 space-y-2">
                            <li>Suspend or terminate accounts that violate these terms.</li>
                            <li>Modify or discontinue services with reasonable notice.</li>
                            <li>Update terms as needed with user notification.</li>
                            <li>Remove inappropriate or inaccurate content.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl text-white font-semibold mb-4">12.&nbsp;Limitations&nbsp;of&nbsp;Liability</h2>
                        <p>olaid and its affiliates are not liable for:</p>
                        <ul className="list-disc pl-6 mt-3 space-y-2">
                            <li>Service interruptions or technical issues.</li>
                            <li>Accuracy of user-provided information.</li>
                            <li>Actions taken by emergency responders.</li>
                            <li>Unauthorized access due to user negligence.</li>
                            <li>Indirect or consequential damages.</li>
                        </ul>
                    </section>
                </div>

                {/* Contact / Help card */}
                <div className="bg-primary/10 rounded-lg p-8 border border-primary/20 max-w-3xl mx-auto mt-12">
                    <h3 className="text-2xl font-semibold text-primary-light mb-4">Need Help or Have Questions?</h3>
                    <p className="text-gray-300">
                        If you have any questions about our Privacy Policy, Terms of Service, or how we handle your data,
                        please contact our support team at&nbsp;
                        <a href="mailto:hi@olaid.me" className="underline text-primary-light">hi@olaid.me</a>.
                        Weʼre committed to transparency and are here to help.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PrivacyAndTerms;
